import { FlexColumn, LabeledInput, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, REQUIRED_FIELD_TEXT } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { createForm } from '../../services/formsService';
import { selectedFormIdState, updateFormsState } from '../../storage/formStates';
import { FormActionsRow } from '../shared/FormActionsRow';
import StyledModal from '../shared/StyledModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function NewFormModal({ isOpen, onClose }: Readonly<Props>) {
  const { css } = useStyles();
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const { loading, startLoading, stopLoading } = useLoading();
  const canSubmit = !!name;
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const setSelectedFormId = useSetRecoilState(selectedFormIdState);
  const setUpdateForms = useSetRecoilState(updateFormsState);

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  const clearStates = () => {
    setName('');
    setNameTouched(false);
  };

  const onSubmit = async () => {
    startLoading();
    const { id: createdFormId, error } = await createForm({
      name,
      tenants: [],
    });
    if (!error && createdFormId) {
      setSelectedFormId(createdFormId);
      showPositiveFeedback(FEEDBACK.created(FEEDBACK_PREFIXES.form));
      setUpdateForms(createdFormId);
      onCloseModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedCreation(FEEDBACK_PREFIXES.form));
    }
    stopLoading();
  };

  const onCloseModal = () => {
    clearStates();
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title="Nuevo formulario"
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '525px',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            <LabeledInput
              placeholder="Ingresa el nombre del formulario"
              value={name}
              onChange={(value) => setName(value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !name && REQUIRED_FIELD_TEXT}
              required
            />
          </FlexColumn>
          <FormActionsRow
            canSubmit={canSubmit}
            loading={loading}
            onSubmit={onSubmit}
            onCancel={onCloseModal}
          />
        </FlexColumn>
      }
    />
  );
}
